import { useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { getNoteList, deleteNote, NoteItem } from "../apis/noteService";

const NoteList = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadNotes = async () => {
      if (!token) return;

      try {
        const noteList = await getNoteList(token);
        setNotes(noteList);
      } catch (error) {
        console.error("Error loading notes:", error);
        alert("連線網址有誤");
      } finally {
        setIsLoading(false);
      }
    };

    loadNotes();
  }, [token]);

  const handleAddNote = () => {
    navigate(`/noteAdd/${token}`);
  };

  const handleMenuToggle = (noteId: string) => {
    setOpenMenuId(openMenuId === noteId ? null : noteId);
  };

  const handleEdit = (noteUuid: string) => {
    navigate(`/noteEdit/${token}/${noteUuid}`);
    setOpenMenuId(null);
  };

  const handleDelete = async (noteUuid: string) => {
    if (window.confirm("確認刪除該記事資料")) {
      if (!token) return;

      try {
        await deleteNote(token, noteUuid);
        // 刪除成功後重新取得記事列表
        const noteList = await getNoteList(token);
        setNotes(noteList);
      } catch (error) {
        console.error("Error deleting note:", error);
        alert("刪除失敗，請重試");
      }
    }
    setOpenMenuId(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 py-5">
        {/* Header with + button */}
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-800">記事本</h1>
          <button
            onClick={handleAddNote}
            className="w-10 h-10 bg-[#C83862] text-white rounded-full flex items-center justify-center text-2xl hover:bg-[#A02850] transition-colors"
          >
            +
          </button>
        </div>

        {/* Loading state */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="text-gray-500">載入中...</div>
          </div>
        ) : (
          <>
            {/* Notes List */}
            <div className="space-y-1">
              {notes.map((note) => (
                <div
                  key={note.uuid}
                  className="bg-white border border-gray-200 rounded-lg p-4 mb-3 last:mb-0"
                >
                  <div className="flex flex-col">
                      <pre className="text-gray-700 mb-3 font-sans whitespace-pre-wrap leading-relaxed">
                        {note.content}
                      </pre>
                    <div className="flex justify-between items-center">
                      <time className="text-sm text-[#00000099]">
                        {new Date(note.updatedAt).toLocaleString('zh-TW', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false
                        })}
                      </time>
                      <div className="relative">
                        <button
                          onClick={() => handleMenuToggle(note.uuid)}
                          className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                        >
                          ⋯
                        </button>
                        {openMenuId === note.uuid && (
                          <div
                            className="absolute right-0 top-8 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-20">
                            <button
                              onClick={() => handleEdit(note.uuid)}
                              className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 transition-colors"
                            >
                              編輯
                            </button>
                            <button
                              onClick={() => handleDelete(note.uuid)}
                              className="block w-full text-left px-4 py-2 text-red-600 hover:bg-gray-100 transition-colors"
                            >
                              刪除
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty state */}
            {notes.length === 0 && (
              <div className="text-center py-12">
                <p className="text-gray-500">尚無記事</p>
                <p className="text-sm text-gray-400 mt-2">點擊右上角的 + 開始新增記事</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NoteList;